import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "@/utils/axios"
import { UpdateUserParameterType } from "../applicationServices/user"
import { UserResponseType } from "../responseBuilders/user"
import { useState } from "react"
import { createClient } from "../../utils/supabase/component"

class UserApi {
  public static async get(id?: string) {
    const res = await axios.get<UserResponseType>(`/v1.0/users/${id}`)
    return res.data
  }
  public static async patch({
    id,
    params
  }: {
    id: string
    params: Omit<UpdateUserParameterType, "userId">
  }) {
    const res = await axios.patch<UserResponseType>(
      `/v1.0/users/${id}`,
      params
    )
    return res.data
  }

  public static async getApiToken() {
    const res = await axios.get<{ token: string }>(`/auth/get-token`)
    return res.data
  }

  public static async generateApiToken() {
    const res = await axios.post<{ token: string }>(`/auth/generate-token`)
    return res.data
  }
}

export const useGetUser = (id?: string) => {
  return useQuery({
    queryKey: ["user", id],
    queryFn: () => (id ? UserApi.get(id) : Promise.resolve(null)),
    enabled: !!id,
    staleTime: Infinity,
    gcTime: Infinity
  })
}

type InvalidateQuery = {
  invalidateGetUser?: boolean
}

export const useUpdateUser = (params: InvalidateQuery) => {
  const { invalidateGetUser = false } = params
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      userId,
      params
    }: { userId: string; params: Omit<UpdateUserParameterType, "userId"> }) =>
      UserApi.patch({ id: userId, params }),
    onSettled: async (_1, _2, variable) => {
      if (invalidateGetUser) {
        await queryClient.invalidateQueries({
          queryKey: ["user", variable.userId]
        })
      }
    }
  })
}

export const useUpdateUserMetadata = () => {
  const supabase = createClient()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updateUserMetadata = async (metadata: { [key: string]: any }) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, error } = await supabase.auth.updateUser({
        data: metadata
      })

      if (error) throw error

      return data.user
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError("An unknown error occurred")
      }
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return { updateUserMetadata, isLoading, error }
}

export const useGetApiToken = () => {
  return useQuery({
    queryKey: ["apiToken"],
    queryFn: () => UserApi.getApiToken()
  })
}

export const useGenerateApiToken = () => {
  return useMutation({
    mutationFn: () => UserApi.generateApiToken()
  })
}
